import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Snowflake, Fish, Ticket, Waves, Anchor, FishSymbol, LocateFixed } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLang } from "../components/LangContext";
import { STR } from "../lib/i18n";
import { api, API } from "../lib/api";
import { CATEGORY_FALLBACK_IMG, HERO_IMG, formatDate } from "../lib/categories";

const CARD_ICONS = [Snowflake, Fish, Ticket, Waves, Anchor, FishSymbol, LocateFixed];
const CARD_KEYS = ["ice", "hotspots", "fvof", "hydro", "depth", "fish", "gps"];
const NEWS_IMGS = [CATEGORY_FALLBACK_IMG.fiske, HERO_IMG, CATEGORY_FALLBACK_IMG.skapande];

export default function Landing() {
  const { lang } = useLang();
  const t = STR[lang];

  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api
      .get("/posts", { params: { limit: 3, lang } })
      .then((r) => setArticles(r.data))
      .catch(() => {});
    api
      .get("/news", { params: { limit: 3, lang } })
      .then((r) => setNews(r.data))
      .catch(() => {});
  }, [lang]);

  return (
    <div data-testid="landing-page" className="bg-[#F6F5F0]">
      <Navbar />

      {/* SEKTION 1: HERO */}
      <section
        data-testid="hero-section"
        className="relative flex min-h-[90vh] items-center justify-center overflow-hidden bg-[#0F1412] text-center text-[#F6F5F0]"
      >
        <img src={HERO_IMG} alt="Dimmig tallskog i gryningen" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0F1412]/65" />
        <div className="relative mx-auto max-w-[1200px] px-8 py-8">
          <Reveal>
            <h1
              data-testid="hero-title"
              className="mx-auto max-w-4xl font-serif text-4xl font-medium leading-[1.12] tracking-tight sm:text-5xl lg:text-6xl"
            >
              {t.hero.title}
            </h1>
            <h3 data-testid="hero-subtitle" className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#EBE9E0]/85 md:text-lg">
              {t.hero.sub}
            </h3>
            <div className="mt-10">
              <Link
                to="/verktyg"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#C98755] px-8 py-4 text-sm font-semibold tracking-wide text-[#0F1412] transition-colors duration-300 hover:bg-[#F6F5F0]"
              >
                {t.hero.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SEKTION 2: FUNKTIONSKORT */}
      <section data-testid="feature-cards-section" className="mx-auto max-w-[1200px] px-8 py-8 mt-16">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {t.featureCards.map((label, i) => {
            const Icon = CARD_ICONS[i];
            return (
              <Reveal key={CARD_KEYS[i]} delay={0.05 * i}>
                <Link
                  to="/verktyg"
                  data-testid={`feature-card-${CARD_KEYS[i]}`}
                  className="group flex h-full flex-col items-center justify-center gap-4 border border-[#D8D3C4] bg-[#EFEBE1] p-8 text-center transition-colors duration-300 hover:border-[#C98755] hover:bg-[#F6F5F0]"
                >
                  <Icon className="h-6 w-6 text-[#9E643E] transition-colors duration-300 group-hover:text-[#C98755]" strokeWidth={1.5} />
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-[#1A221E]">{label}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SEKTION 3: NYHETER */}
      {news.length > 0 && (
        <section data-testid="news-section" className="mt-16 border-y border-[#D8D3C4] bg-[#EFEBE1]">
          <div className="mx-auto max-w-[1200px] px-8 py-8">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-[#1A221E] sm:text-4xl">{t.homeNews.title}</h2>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {news.map((a, i) => (
                <Reveal key={a.url} delay={0.06 * i}>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`news-card-${i}`}
                    className="group block h-full border border-[#D8D3C4] bg-[#F6F5F0] transition-colors duration-300 hover:border-[#C98755]"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={a.image || NEWS_IMGS[i % NEWS_IMGS.length]}
                        alt=""
                        loading="lazy"
                        className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <p className="overline-label flex items-center gap-2 text-[#9E643E]">
                        {a.country && (
                          <span className="inline-flex items-center rounded-full border border-[#9E643E]/40 px-2 py-0.5 text-[10px] tracking-widest">
                            {a.country}
                          </span>
                        )}
                        {a.source}
                      </p>
                      <h3 className="mt-3 line-clamp-3 font-serif text-xl font-medium leading-snug tracking-tight text-[#1A221E] transition-colors duration-300 group-hover:text-[#9E643E]">
                        {a.title}
                      </h3>
                      <p className="mt-3 text-xs tracking-widest text-[#7B8A80]">{formatDate(a.published, lang)}</p>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="mt-8 text-center">
                <Link
                  to="/nyheter"
                  data-testid="news-view-all-button"
                  className="group inline-flex items-center gap-3 rounded-full border border-[#2D4032] px-8 py-4 text-sm font-semibold tracking-wide text-[#2D4032] transition-colors duration-300 hover:bg-[#2D4032] hover:text-[#F6F5F0]"
                >
                  {t.homeNews.viewAll}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* SEKTION 4: REPORTAGE / ARTIKLAR */}
      {articles.length > 0 && (
        <section data-testid="articles-section" className="mx-auto mt-16 max-w-[1200px] px-8 py-8">
          <Reveal>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-[#1A221E] sm:text-4xl">{t.homeArticles.title}</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((post, i) => (
              <Reveal key={post.id} delay={0.06 * i}>
                <Link
                  to={`/reportage/${post.slug}`}
                  data-testid={`article-card-${post.slug}`}
                  className="group block h-full border border-[#D8D3C4] bg-[#EFEBE1] transition-colors duration-300 hover:border-[#C98755]"
                >
                  <div className="overflow-hidden">
                    <img
                      src={post.images?.[0] ? `${API}/files/${post.images[0]}` : CATEGORY_FALLBACK_IMG[post.category]}
                      alt={post.title}
                      loading="lazy"
                      className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <p className="overline-label text-[#9E643E]">{t.categories[post.category].label}</p>
                    <h3 className="mt-3 font-serif text-xl font-medium leading-snug tracking-tight text-[#1A221E] transition-colors duration-300 group-hover:text-[#9E643E]">
                      {post.title}
                    </h3>
                    {post.excerpt && <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-[#1A221E]/70">{post.excerpt}</p>}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-8 text-center">
              <Link
                to="/reportage"
                data-testid="articles-view-all-button"
                className="group inline-flex items-center gap-3 rounded-full border border-[#2D4032] px-8 py-4 text-sm font-semibold tracking-wide text-[#2D4032] transition-colors duration-300 hover:bg-[#2D4032] hover:text-[#F6F5F0]"
              >
                {t.homeArticles.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </Link>
            </div>
          </Reveal>
        </section>
      )}

      {/* SEKTION 5: OM SPORTFISKEVATTEN */}
      <section data-testid="about-section" className="mt-16 border-t border-[#D8D3C4] bg-[#EFEBE1]">
        <div className="mx-auto max-w-[1200px] px-8 py-8 text-center md:max-h-[400px]">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-serif text-3xl font-medium tracking-tight text-[#1A221E] sm:text-4xl">
              {t.aboutSection.title}
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#1A221E]/75 md:text-lg">{t.aboutSection.text}</p>
          </Reveal>
        </div>
      </section>

      {/* SEKTION 6: FOOTER */}
      <div className="mt-16">
        <Footer />
      </div>
    </div>
  );
}
